import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { logError } from "@/lib/logger";

interface ConfirmDialogProps {
	title: string;
	message?: string;
	confirmLabel: string;
	cancelLabel?: string;
	destructive?: boolean;
	onConfirm: () => void | Promise<void>;
	onCancel: () => void;
}

export function ConfirmDialog({
	title,
	message,
	confirmLabel,
	cancelLabel,
	destructive = false,
	onConfirm,
	onCancel,
}: ConfirmDialogProps) {
	const { t } = useTranslation();
	const resolvedCancelLabel = cancelLabel ?? t("common.cancel");
	const cancelRef = useRef<HTMLButtonElement>(null);
	const [confirming, setConfirming] = useState(false);

	useEffect(() => {
		cancelRef.current?.focus();
	}, []);

	async function handleConfirm() {
		if (confirming) return;
		setConfirming(true);
		try {
			await onConfirm();
		} catch (error) {
			logError("Confirmation action failed", error);
		} finally {
			setConfirming(false);
		}
	}

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				onCancel();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [onCancel]);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
			onClick={() => {
				if (!confirming) onCancel();
			}}
		>
			<div
				className="bg-[#141415] border border-[#1c1c1e] rounded-lg p-4 w-80 flex flex-col gap-3"
				onClick={(e) => e.stopPropagation()}
			>
				<p className="text-sm font-medium text-foreground">{title}</p>
				{message && (
					<p className="text-xs text-muted-foreground leading-relaxed">
						{message}
					</p>
				)}
				<div className="flex gap-2 justify-end mt-1">
					<button
						ref={cancelRef}
						type="button"
						disabled={confirming}
						onClick={onCancel}
						className="text-xs text-muted-foreground hover:text-foreground px-3 py-1"
					>
						{resolvedCancelLabel}
					</button>
					<button
						type="button"
						disabled={confirming}
						onClick={() => void handleConfirm()}
						className={
							destructive
								? "text-xs rounded px-3 py-1 font-medium bg-[#ffb4ab] text-[#141415] hover:bg-[#ffb4ab]/80"
								: "text-xs rounded px-3 py-1 bg-primary text-primary-foreground hover:bg-primary/80"
						}
					>
						{confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
}
